import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  //   IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from "@mui/material";
import { Edit, Delete, EventRepeat } from "@mui/icons-material";
import { getActivityById } from "../../api/planning";
import { useSnackbar } from "notistack";
import { format } from "date-fns";
import useAuth from "../../hooks/useAuth";

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isSuperAdmin = Boolean(
    user?.roles?.some((r) => r === "super_admin" || r?.name === "super_admin")
  );
  const farmTenantParam = useMemo(() => {
    if (!isSuperAdmin) return null;
    const raw = searchParams.get("tenant_id");
    if (raw == null || raw === "") return null;
    const tid = Number(raw);
    return Number.isNaN(tid) ? null : tid;
  }, [isSuperAdmin, searchParams]);

  const planningSearchSuffix = searchParams.toString()
    ? `?${searchParams.toString()}`
    : "";

  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const data = await getActivityById(id, {
          tenantId: farmTenantParam,
          includeDeleted: true,
        });
        setActivity(data);
      } catch (err) {
        setError(err.message || "Failed to fetch activity details");
        enqueueSnackbar("Failed to load activity details", {
          variant: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [id, enqueueSnackbar, farmTenantParam]);

  const getStatusColor = (status) => {
    switch (status) {
      case "Planned":
        return "primary";
      case "In Progress":
        return "warning";
      case "Completed":
        return "success";
      case "Cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "Low":
        return "success";
      case "Medium":
        return "warning";
      case "High":
        return "error";
      default:
        return "default";
    }
  };

  const handleDelete = async () => {
    try {
      // await deleteActivity(id); // Uncomment when delete endpoint is available
      enqueueSnackbar("Activity deleted successfully", { variant: "success" });
      navigate("/planning/activities");
    } catch (error) {
      enqueueSnackbar("Failed to delete activity", { variant: "error" });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!activity) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning">Activity not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography variant="h4">{activity.name}</Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="outlined"
            component={Link}
            to={`/planning/activities${planningSearchSuffix}`}
            fullWidth={undefined}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Back to List
          </Button>
          <Button
            variant="contained"
            color="secondary"
            component={Link}
            to={`/planning/activities/${activity.id}/edit${planningSearchSuffix}`}
            startIcon={<Edit />}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteDialogOpen(true)}
            startIcon={<Delete />}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Activity Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              <ListItem>
                <ListItemText primary="Name" secondary={activity.name} />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Description"
                  secondary={activity.description || "No description provided"}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Activity Type"
                  secondary={activity.activity_type}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Created"
                  secondary={format(
                    new Date(activity.created_at),
                    "MMMM d, yyyy"
                  )}
                />
              </ListItem>
              {activity.template_id && (
                <ListItem>
                  <ListItemText
                    primary="Created From Template"
                    secondary={`Template ID: ${activity.template_id}`}
                  />
                </ListItem>
              )}
            </List>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Schedule & Status
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Start Date"
                  secondary={format(
                    new Date(activity.start_date),
                    "MMMM d, yyyy"
                  )}
                />
              </ListItem>
              {!activity.is_all_day && activity.end_date && (
                <ListItem>
                  <ListItemText
                    primary="End Date"
                    secondary={format(
                      new Date(activity.end_date),
                      "MMMM d, yyyy"
                    )}
                  />
                </ListItem>
              )}
              <ListItem>
                <ListItemText
                  primary="Priority"
                  secondary={
                    <Chip
                      label={activity.priority}
                      color={getPriorityColor(activity.priority)}
                      size="small"
                    />
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Status"
                  secondary={
                    <Chip
                      label={activity.status}
                      color={getStatusColor(activity.status)}
                      size="small"
                    />
                  }
                />
              </ListItem>
              <ListItem>
                <ListItemText primary="Season" secondary={activity.season} />
              </ListItem>
              {activity.is_recurring && (
                <ListItem>
                  <ListItemText
                    primary="Recurrence"
                    secondary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography>
                          {activity.recurrence_pattern} (every{" "}
                          {activity.recurrence_interval}{" "}
                          {activity.recurrence_pattern.toLowerCase()})
                        </Typography>
                        <EventRepeat fontSize="small" />
                      </Box>
                    }
                  />
                </ListItem>
              )}
              {activity.is_recurring && activity.recurrence_end_date && (
                <ListItem>
                  <ListItemText
                    primary="Recurrence End Date"
                    secondary={format(
                      new Date(activity.recurrence_end_date),
                      "MMMM d, yyyy"
                    )}
                  />
                </ListItem>
              )}
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* Resource Assignment Section */}
      <Typography variant="h5" gutterBottom>
        Assigned Resources
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography color="text.secondary">
          Resource assignment functionality will be implemented in the next
          phase.
        </Typography>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this activity? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityDetail;
